import { orderTotal } from "@/utils/guestOrderTotal";
import { DELIVERY_TYPES, ORDER_STATUSES, isActiveState, menuState } from "@/utils/guestOrderStatus";
import hasGuestOrderPanel from "./hasGuestOrderPanel";

const LIVE_MS = 30000;
const TICK_MS = 5000;
/** Tekst za pretragu jedne narudžbe: gost, telefon, broj narudžbe, adresa, restoran i nazivi jela (i izbora). */
const searchTextOf = (guest, order) =>
  [
    guest.name,
    guest.lastname,
    guest.phone,
    guest.email,
    order.id,
    order.location?.address,
    order.restaurant?.name,
    ...(order.order_guest_transactions || []).flatMap((transaction) => [
      transaction.food?.name || transaction.meals?.name,
      ...(transaction.prilog || []).map((choice) => choice.food?.name || choice.meals?.name),
    ]),
  ]
    .filter(Boolean)
    .join(" ");

/**
 * Ekran "Narudžbe kupaca" za novu ljusku. Koristi se uz `extends` starog taba (ReportTab za restoran,
 * AdminReportTab za admina): odatle dolaze učitavanje, promena statusa, štampa i Excel, a ovde je
 * ono što je novo: jedna narudžba po redu, filteri po statusu i vrsti isporuke, brojke u zaglavlju,
 * panel sa detaljima i osvežavanje "uživo". Štampa i Excel dobijaju iste (filtrirane) redove koje
 * vidi korisnik.
 */
export default {
  mixins: [hasGuestOrderPanel],
  props: {
    /** Izabran status ("" = svi) */
    status: { type: String, default: "" },
    /** Izabrana vrsta isporuke (null = sve) */
    delivery: { type: Number, default: null },
  },
  data: () => ({
    failed: false,
    live: false,
    refreshedAt: 0,
    now: Date.now(),
  }),
  computed: {
    isAdmin() {
      return this.$store.state.user.user?.type === "admin";
    },
    /** Datum → redovi, jedna narudžba po redu (gost sa više narudžbi dobija više redova). */
    flatDays() {
      const days = {};
      Object.entries(this.processedData || {}).forEach(([date, guests]) => {
        const rows = [];
        guests.forEach((guest) => {
          (guest.orders_guest || []).forEach((order) => {
            rows.push({ ...guest, orders_guest: [order], searchText: searchTextOf(guest, order) });
          });
        });
        if (rows.length) {
          days[date] = rows;
        }
      });
      return days;
    },
    /** Posle vrste isporuke i pretrage, a pre statusa (od ovoga se računaju brojači statusa). */
    searchedDays() {
      const days = {};
      Object.entries(this.flatDays).forEach(([date, rows]) => {
        const byDelivery = rows.filter(
          (row) => this.delivery === null || row.orders_guest[0].delivery_type === this.delivery
        );
        const matched = this.customSearch(byDelivery, [{ value: "searchText" }], this.searchQuery);
        if (matched.length) {
          days[date] = matched;
        }
      });
      return days;
    },
    /** Redovi koje korisnik vidi (i koji se štampaju i izvoze u Excel). */
    filteredData() {
      if (!this.status) {
        return this.searchedDays;
      }
      const days = {};
      Object.entries(this.searchedDays).forEach(([date, rows]) => {
        const matched = rows.filter((row) => menuState(row.orders_guest[0].state) === this.status);
        if (matched.length) {
          days[date] = matched;
        }
      });
      return days;
    },
    searchedOrders() {
      return Object.values(this.searchedDays).flat();
    },
    visibleOrders() {
      return Object.values(this.filteredData).flat();
    },
    hasOrders() {
      return Object.keys(this.flatDays).length > 0;
    },
    /** [{ state, count }] u redosledu menija, pa nepoznati statusi */
    statusCounts() {
      const counts = {};
      this.searchedOrders.forEach((row) => {
        const state = menuState(row.orders_guest[0].state);
        counts[state] = (counts[state] || 0) + 1;
      });
      const known = ORDER_STATUSES.map((item) => item.value).filter((state) => counts[state]);
      const other = Object.keys(counts).filter((state) => !known.includes(state));
      return [...known, ...other].map((state) => ({ state, count: counts[state] }));
    },
    /** Vrste isporuke koje postoje u podacima (posle statusa, pretrage nije) */
    deliveryCounts() {
      const counts = {};
      Object.values(this.flatDays)
        .flat()
        .forEach((row) => {
          const type = row.orders_guest[0].delivery_type;
          counts[type] = (counts[type] || 0) + 1;
        });
      return DELIVERY_TYPES.filter((type) => counts[type.id]).map((type) => ({
        ...type,
        count: counts[type.id],
      }));
    },
    /** Ukupan iznos po valuti restorana čija je narudžba (različite valute se ne sabiraju). */
    totalParts() {
      const buckets = {};
      this.visibleOrders.forEach((row) => {
        const order = row.orders_guest[0];
        const currency = order.restaurant?.settings?.price || this.restaurantCurrency;
        buckets[currency] = (buckets[currency] || 0) + orderTotal(order);
      });
      return Object.entries(buckets).map(([currency, amount]) => ({ currency, amount }));
    },
    kpis() {
      const total = this.totalParts.length
        ? this.totalParts.map((part) => `${part.amount.toFixed(2)} ${part.currency}`).join(" + ")
        : `0.00 ${this.restaurantCurrency}`;
      return [
        {
          key: "orders",
          label: this.$t("orders"),
          value: this.visibleOrders.length,
          tone: "inverse",
        },
        { key: "total", label: this.$t("total"), value: total },
        {
          key: "active",
          label: this.$t("guest_desktop.active"),
          value: this.visibleOrders.filter((row) => isActiveState(row.orders_guest[0].state)).length,
          tone: "accent",
        },
      ];
    },
    liveText() {
      if (!this.live) {
        return this.$t("report_desktop.live_off");
      }
      const seconds = Math.max(0, Math.round((this.now - this.refreshedAt) / 1000));
      return this.$t("report_desktop.live_on", { s: seconds });
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.hasOrders ? kpis : []);
      },
      immediate: true,
    },
    isLoading(loading) {
      this.$emit("loading", loading);
    },
    live(on) {
      if (on) {
        this.startLive();
      } else {
        this.stopLive();
      }
    },
  },
  beforeDestroy() {
    this.stopLive();
  },
  methods: {
    startLive() {
      this.refreshedAt = Date.now();
      this.now = this.refreshedAt;
      this.liveTimer = setInterval(() => {
        if (!document.hidden) {
          this.loadReportData({ silent: true });
        }
      }, LIVE_MS);
      this.tickTimer = setInterval(() => {
        this.now = Date.now();
      }, TICK_MS);
    },
    stopLive() {
      clearInterval(this.liveTimer);
      clearInterval(this.tickTimer);
    },
    markLoaded() {
      this.refreshedAt = Date.now();
      this.now = this.refreshedAt;
    },
  },
};
